import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { MMTradesEndpointEnum } from "../../../enums/endpoints";
import { MMTRADES_API_URL } from "../../../utils/env";
import { AuthContext } from "../../../context/AuthContext";

interface IAccountBalanceHistoryResponse {
  success: boolean;
  data: IAccountBalance[];
}

export interface IAccountBalance {
  Timestamp: string;
  Exchange: string;
  Strategy: string;
  AccountName: string;
  USDBalance: string;
}

export const useAccountBalanceHistory = (
  start_time: string,
  end_time?: string,
  strategy?: string
) => {
  const { tokenId } = useContext(AuthContext);
  const [data, setData] = useState<IAccountBalance[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Only show loading on initial fetch, not on refreshes
    if (data === undefined) {
      setIsLoading(true);
    }

    let cancelled = false;
    const fetchBalanceHistory = async () => {
      const params: { [key: string]: any } = { start_time };
      if (end_time) params.end_time = end_time;
      if (strategy) params.strategy = strategy;

      try {
        const response = await axios.get<IAccountBalanceHistoryResponse>(
          MMTRADES_API_URL(MMTradesEndpointEnum.ACCOUNT_BALANCE_HISTORY),
          {
            timeout: 10_000,
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${tokenId}`,
              "Access-Control-Allow-Origin": "*",
            },
            params,
          }
        );

        if (!response.data.success || !Array.isArray(response.data.data)) throw new Error("Invalid balance response");
        if (!cancelled) {
          setData(response.data.data);
          setError(null);
        }
      } catch {
        if (!cancelled) setError("Balance history fetch failed");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (tokenId) {
      fetchBalanceHistory();
      const timer = setInterval(fetchBalanceHistory, 60_000);
      return () => { cancelled = true; clearInterval(timer); };
    }
    return () => { cancelled = true; };
  }, [tokenId, start_time, end_time, strategy]);

  return { data, isLoading, error };
};
